import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ContentfulPromotionBannerType } from '@reward-platform/contentful'
import { usePlatformConfig } from '~/context/platform'
import { useLocalisation } from '~/context/localisation'
import { useNotification } from '~/context/notification'
import { useFeatureFlag } from '~/context/featureFlag'
import { GenericClientError } from '~/utils/errors'
import { getPromotionBanners } from '~/services/contentService'
import { getPromotionUxSignal } from '~/services/promotionsService'
import { PromotionUxSignalProduct } from '~/schemas/promotionUxSignal'
import {
  PromotionalBannerData,
  BannerPageType,
} from '~/components/shared/PromotionalBanners/PromotionalBanners.types'
import { useContentfulPreviewMode } from '../useContentfulPreviewMode/useContentfulPreviewMode'

export const CUG_CALLOUT_BANNER_ELIGIBILITY_FLAG = 'cug-callout-banner-eligibility'
export const STANDALONE_PROMOTION = 'STANDALONE_PROMOTION'

const isStandalonePromotionIdentifier = (identifier?: string): boolean =>
  identifier == null || identifier === STANDALONE_PROMOTION

export const isPromotionLinkedIdentifier = (identifier?: string): identifier is string =>
  Boolean(identifier) && !isStandalonePromotionIdentifier(identifier)

type ContentfulProduct = 'Hotel' | 'Car' | 'Experience'

type ProductConfig = {
  contentfulProduct: ContentfulProduct
  uxSignalProduct: PromotionUxSignalProduct
}

const PRODUCT_CONFIG_MAP: Record<string, ProductConfig> = {
  hotels: { contentfulProduct: 'Hotel', uxSignalProduct: 'HOTEL' },
  HOTEL: { contentfulProduct: 'Hotel', uxSignalProduct: 'HOTEL' },
  cars: { contentfulProduct: 'Car', uxSignalProduct: 'CAR_HIRE' },
  carhire: { contentfulProduct: 'Car', uxSignalProduct: 'CAR_HIRE' },
  CAR_HIRE: { contentfulProduct: 'Car', uxSignalProduct: 'CAR_HIRE' },
  experiences: { contentfulProduct: 'Experience', uxSignalProduct: 'EXPERIENCE' },
  EXPERIENCE: { contentfulProduct: 'Experience', uxSignalProduct: 'EXPERIENCE' },
}

const EMPTY_ELIGIBLE_PROMOTION_IDS = new Set<string>()

const toBannerData = (banner: ContentfulPromotionBannerType): PromotionalBannerData => ({
  id: banner.identifier,
  text: banner.bodyCopy,
  tagLabel: banner.badgeCopy,
  promotionId: banner.promotionUniqueIdentifier,
})

const useContentfulPromotionBanners = () => {
  const { opCo } = usePlatformConfig()
  const { locale } = useLocalisation()
  const { addErrorCodeNotification } = useNotification()
  const previewMode = useContentfulPreviewMode()

  const { data, error, isError } = useQuery({
    queryKey: ['promotionBanners', opCo, locale ?? 'en-US', previewMode],
    enabled: Boolean(opCo),
    queryFn: () => getPromotionBanners(opCo, locale, previewMode),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })

  useEffect(() => {
    if (isError && error instanceof GenericClientError) {
      addErrorCodeNotification('CONTENT_PROMOTION_BANNERS_ERROR', {
        type: 'ERROR',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  return { banners: data, isError }
}

const usePromotionUxSignal = (product?: PromotionUxSignalProduct, enabled = true) => {
  const { opCo } = usePlatformConfig()

  const { data, isError, isFetching } = useQuery({
    queryKey: ['promotionUxSignal', opCo, product ?? 'ALL_PRODUCTS'],
    enabled: Boolean(opCo) && enabled,
    queryFn: () => getPromotionUxSignal(product),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })

  return {
    eligiblePromotionIds: data ? new Set(data.promotions) : undefined,
    isError,
    isFetching,
  }
}

const matchesProductAndLocation = (
  banner: ContentfulPromotionBannerType,
  contentfulProduct: ContentfulProduct,
  page: BannerPageType
): boolean => banner.product === contentfulProduct && banner.location.includes(page)

const isVisiblePromotionBanner = (
  banner: ContentfulPromotionBannerType,
  eligiblePromotionIds: Set<string>,
  page: BannerPageType
): boolean => {
  const identifier = banner.promotionUniqueIdentifier

  if (page === 'basket') {
    return isPromotionLinkedIdentifier(identifier) && eligiblePromotionIds.has(identifier)
  }

  return (
    isStandalonePromotionIdentifier(identifier) ||
    (isPromotionLinkedIdentifier(identifier) && eligiblePromotionIds.has(identifier))
  )
}

const selectContentfulBanners = (
  banners: ContentfulPromotionBannerType[],
  contentfulProduct: ContentfulProduct,
  page: BannerPageType
): PromotionalBannerData[] =>
  banners
    .filter((banner) => matchesProductAndLocation(banner, contentfulProduct, page))
    .sort((a, b) => a.priority - b.priority)
    .map(toBannerData)

const selectEligibleBanners = (
  banners: ContentfulPromotionBannerType[],
  eligiblePromotionIds: Set<string>,
  contentfulProduct: ContentfulProduct,
  page: BannerPageType
): PromotionalBannerData[] =>
  banners
    .filter(
      (banner) =>
        matchesProductAndLocation(banner, contentfulProduct, page) &&
        isVisiblePromotionBanner(banner, eligiblePromotionIds, page)
    )
    .sort((a, b) => a.priority - b.priority)
    .map(toBannerData)

const resolveVisiblePromotionIds = (
  eligiblePromotionIds: Set<string> | undefined,
  isUxSignalError: boolean,
  isUxSignalFetching: boolean
): Set<string> => {
  if (!eligiblePromotionIds || isUxSignalError || isUxSignalFetching) {
    return EMPTY_ELIGIBLE_PROMOTION_IDS
  }

  return eligiblePromotionIds
}

const usePromotionBanners = (product: string, page: BannerPageType): PromotionalBannerData[] => {
  const isCugEligibilityEnabled = useFeatureFlag(CUG_CALLOUT_BANNER_ELIGIBILITY_FLAG)
  const productConfig = PRODUCT_CONFIG_MAP[product]

  const { banners, isError } = useContentfulPromotionBanners()
  const {
    eligiblePromotionIds,
    isError: isUxSignalError,
    isFetching: isUxSignalFetching,
  } = usePromotionUxSignal(
    productConfig?.uxSignalProduct,
    Boolean(productConfig) && isCugEligibilityEnabled
  )

  return useMemo(() => {
    if (!Array.isArray(banners) || !productConfig || isError) {
      return []
    }

    if (!isCugEligibilityEnabled) {
      return selectContentfulBanners(banners, productConfig.contentfulProduct, page)
    }

    return selectEligibleBanners(
      banners,
      resolveVisiblePromotionIds(eligiblePromotionIds, isUxSignalError, isUxSignalFetching),
      productConfig.contentfulProduct,
      page
    )
  }, [
    banners,
    eligiblePromotionIds,
    productConfig,
    page,
    isError,
    isUxSignalError,
    isUxSignalFetching,
    isCugEligibilityEnabled,
  ])
}

export type BasketPromotionBanners = {
  hotelBanners: PromotionalBannerData[]
  carBanners: PromotionalBannerData[]
  experienceBanners: PromotionalBannerData[]
}

const NO_BASKET_BANNERS: BasketPromotionBanners = {
  hotelBanners: [],
  carBanners: [],
  experienceBanners: [],
}

export const useBasketPromotionBanners = (): BasketPromotionBanners => {
  const isCugEligibilityEnabled = useFeatureFlag(CUG_CALLOUT_BANNER_ELIGIBILITY_FLAG)
  const { banners, isError } = useContentfulPromotionBanners()
  const {
    eligiblePromotionIds,
    isError: isUxSignalError,
    isFetching: isUxSignalFetching,
  } = usePromotionUxSignal(undefined, isCugEligibilityEnabled)

  return useMemo(() => {
    if (!Array.isArray(banners) || isError) {
      return NO_BASKET_BANNERS
    }

    if (!isCugEligibilityEnabled) {
      return {
        hotelBanners: selectContentfulBanners(banners, 'Hotel', 'basket'),
        carBanners: selectContentfulBanners(banners, 'Car', 'basket'),
        experienceBanners: selectContentfulBanners(banners, 'Experience', 'basket'),
      }
    }

    const visiblePromotionIds = resolveVisiblePromotionIds(
      eligiblePromotionIds,
      isUxSignalError,
      isUxSignalFetching
    )

    const select = (contentfulProduct: ContentfulProduct) =>
      selectEligibleBanners(banners, visiblePromotionIds, contentfulProduct, 'basket')

    return {
      hotelBanners: select('Hotel'),
      carBanners: select('Car'),
      experienceBanners: select('Experience'),
    }
  }, [
    banners,
    eligiblePromotionIds,
    isError,
    isUxSignalError,
    isUxSignalFetching,
    isCugEligibilityEnabled,
  ])
}

export default usePromotionBanners
